import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  FormControl,
  Select,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as XLSX from 'xlsx';
import { getPOsWithInvoices } from './functions/get_pos_with_invoices.function';
import { getPoRingsDetail } from './functions/get_po_rings_detail.function';
import { getAuthorizedPoById } from './functions/get_authorized_po_by_id.function';
import { getPOsWithNonPendingInvoicesWithin6Months } from './functions/get_pos_with_non_pending_invoices_within_6_months.function';
import { getPOsWithNonPendingInvoicesOlderThan6Months } from './functions/get_pos_with_non_pending_invoices_older_than_6_months.function';
import { getPOsWithStoppedInvoices } from './functions/get_pos_with_stopped_invoices.function';
import { getAllVendorInvoices } from './functions/get_all_vendor_invoices.function';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { usePoWorkflowFilters } from './use_po_workflow_filters.hook';

const EXCEL_MAX_CELL_LENGTH = 32767;

const TAB_EXPORT_LABELS = {
  'action-required': { filename: 'Active_POs', sheet: 'Active POs' },
  'stopped-invoices': { filename: 'Stopped_Invoices_POs', sheet: 'Stopped Invoices' },
  'all-invoices': { filename: 'Closed_POs', sheet: 'Closed POs' },
  archive: { filename: 'Archived_POs', sheet: 'Archived POs' },
  'all-invoices-full': { filename: 'All_Invoice', sheet: 'All Invoice' },
};

const getTabExportMeta = (tab) =>
  TAB_EXPORT_LABELS[tab] || { filename: 'Vendor_Invoice', sheet: 'Vendor Invoice' };

const PO_WORKFLOW_EXCEL_EXCLUDED_FIELDS = new Set([
  'assigned_to_role_id',
  'other_documents_paths',
  'po_file_path',
  'previous_assigned_to_role_id',
  'updated_at',
]);

const toExcelSafeValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }
  let text;
  if (typeof value === 'object') {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  } else {
    text = String(value);
  }
  return text.length > EXCEL_MAX_CELL_LENGTH ? text.slice(0, EXCEL_MAX_CELL_LENGTH) : text;
};

/** One Excel row from an API object; nested objects/arrays → JSON strings (full payload export). */
const objectToExcelRow = (obj, excludedFields = PO_WORKFLOW_EXCEL_EXCLUDED_FIELDS) => {
  if (!obj || typeof obj !== 'object') {
    return {};
  }
  const row = {};
  for (const [key, val] of Object.entries(obj)) {
    if (excludedFields.has(key)) {
      continue;
    }
    row[key] = toExcelSafeValue(val);
  }
  return row;
};

/** Ensure every row has the same keys so XLSX includes all columns. */
const alignExcelRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return rows;
  }
  const allKeys = new Set();
  rows.forEach((r) => {
    Object.keys(r).forEach((k) => allKeys.add(k));
  });
  const keys = [...allKeys].sort();
  return rows.map((r) => {
    const out = {};
    keys.forEach((k) => {
      out[k] = toExcelSafeValue(r[k] !== undefined && r[k] !== null ? r[k] : '');
    });
    return out;
  });
};

const ALL_INVOICE_EXCEL_EXCLUDED_FIELDS = new Set([
  'assigned_to_role_id',
  'other_documents_paths',
  'po_file_path',
  'previous_assigned_to_role_id',
  'updated_at',
  'po_date',
]);

/** Shapes all-vendor-invoices API rows for Excel (role names only; omit sensitive/unwanted columns). */
const mapAllVendorInvoiceForExcelExport = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const out = {};
  for (const [key, val] of Object.entries(raw)) {
    if (ALL_INVOICE_EXCEL_EXCLUDED_FIELDS.has(key)) {
      continue;
    }
    if (key === 'assigned_role') {
      const name =
        val && typeof val === 'object' && val.name != null
          ? String(val.name)
          : typeof val === 'string'
            ? val
            : '';
      out.assigned_role = toExcelSafeValue(name);
      continue;
    }
    if (key === 'previous_assigned_role') {
      const name = val && typeof val === 'object' && val.name != null ? String(val.name) : '';
      out.previous_assigned_role = toExcelSafeValue(name);
      continue;
    }
    out[key] = toExcelSafeValue(val);
  }
  return out;
};

/** PO workflow rows: one sheet row per invoice with prefixed PO / invoice fields + assigned_role JSON. */
const expandPoWorkflowRowsForExport = (item) => {
  const po = item._poDetails || {};
  const invs = item._invoices && item._invoices.length > 0 ? item._invoices : [];
  const poRow = Object.fromEntries(
    Object.entries(objectToExcelRow(po)).map(([k, v]) => [`po_${k}`, v])
  );
  if (invs.length === 0) {
    return [{ ...poRow, export_note: 'no_invoices_in_group' }];
  }
  return invs.map(({ invoiceDetails = {}, assignedRole = {} }) => ({
    ...poRow,
    ...Object.fromEntries(
      Object.entries(objectToExcelRow(invoiceDetails)).map(([k, v]) => [`inv_${k}`, v])
    ),
    assigned_role: Object.keys(assignedRole).length ? toExcelSafeValue(assignedRole) : '',
  }));
};

export const POWorkflowTable = ({ selectedTab }) => {
  const navigate = useNavigate();
  const {
    searchTerm,
    selectedDate,
    selectedCity,
    selectedVendor,
    allInvoicesDateFrom,
    allInvoicesDateTo,
    allInvoicesStatus,
    setSearchTerm,
    setSelectedDate,
    setSelectedCity,
    setSelectedVendor,
    setAllInvoicesDateFrom,
    setAllInvoicesDateTo,
    setAllInvoicesStatus,
  } = usePoWorkflowFilters(selectedTab);
  const [allInvoicesData, setAllInvoicesData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [workflowData, setWorkflowData] = useState([]);
  const [rawData, setRawData] = useState([]); // Store raw API data for invoice navigation
  const [loading, setLoading] = useState(false);
  const [viewModal, setViewModal] = useState({
    open: false,
    poData: null,
    rings: [],
    ringsLoading: false,
  });

  // Fetch PO data with invoices from API based on selected tab
  useEffect(() => {
    const fetchPOs = async () => {
      setLoading(true);
      try {
        let response;

        // Call appropriate API based on selected tab
        if (selectedTab === 'action-required') {
          response = await getPOsWithInvoices();
        } else if (selectedTab === 'stopped-invoices') {
          response = await getPOsWithStoppedInvoices();
        } else if (selectedTab === 'all-invoices') {
          response = await getPOsWithNonPendingInvoicesWithin6Months();
        } else if (selectedTab === 'archive') {
          response = await getPOsWithNonPendingInvoicesOlderThan6Months();
        } else if (selectedTab === 'all-invoices-full') {
          const invoices = await getAllVendorInvoices();
          setAllInvoicesData(Array.isArray(invoices) ? invoices : []);
          setRawData([]);
          setWorkflowData([]);
          setLoading(false);
          return;
        } else {
          response = await getPOsWithInvoices();
        }

        setRawData(response);

        // Group by PO number (since API might return multiple items per PO, one per invoice)
        const poMap = new Map();

        response.forEach((item) => {
          const poDetails = item.po_details || {};
          const invoiceDetails = item.invoice_details || {};
          const assignedRole = item.assigned_role || {};
          const poNumber = poDetails.po || poDetails.po_number || 'N/A';

          if (!poNumber || poNumber === 'N/A') {
            // Skip items without valid PO number
            return;
          }

          if (!poMap.has(poNumber)) {
            // First time seeing this PO, create entry
            poMap.set(poNumber, {
              poDetails,
              invoices: [],
              assignedRoles: new Set(),
            });
          }

          // Add invoice to this PO's invoice list (only if invoice_details exists and has data)
          const poEntry = poMap.get(poNumber);
          if (invoiceDetails && invoiceDetails.id && Object.keys(invoiceDetails).length > 0) {
            poEntry.invoices.push({
              invoiceDetails,
              assignedRole,
            });
          }
          if (assignedRole && assignedRole.name) {
            poEntry.assignedRoles.add(assignedRole.name);
          }
        });

        // Map grouped data to table structure
        const mappedData = Array.from(poMap.entries()).map(([poNumber, poData]) => {
          const poDetails = poData.poDetails;
          const invoices = poData.invoices;

          // Determine PO status from all invoice statuses
          let poStatus = 'Pending';
          const hasReverted = invoices.some(
            (inv) => inv.invoiceDetails?.status?.toLowerCase() === 'reverted'
          );
          const hasRejected = invoices.some(
            (inv) => inv.invoiceDetails?.status?.toLowerCase() === 'rejected'
          );
          const allApproved =
            invoices.length > 0 &&
            invoices.every((inv) => {
              const status = inv.invoiceDetails?.status?.toLowerCase();
              return status === 'approved' || status === 'completed';
            });

          if (hasReverted) {
            poStatus = 'Reverted';
          } else if (hasRejected) {
            poStatus = 'Rejected';
          } else if (allApproved) {
            poStatus = 'Approved';
          } else {
            poStatus = 'Pending';
          }

          // Get region from first invoice, assigned role, or poDetails
          const firstInvoice = invoices[0];
          const region =
            firstInvoice?.invoiceDetails?.region || firstInvoice?.assignedRole?.region || 'N/A';

          // Format PO length
          const poLength = poDetails.final_length_km
            ? `${poDetails.final_length_km} km`
            : poDetails.final_length_meters
              ? `${(poDetails.final_length_meters / 1000).toFixed(2)} km`
              : 'N/A';

          // Format unit rate
          const unitRate = poDetails.unit_rate_meter
            ? `PKR ${Number(poDetails.unit_rate_meter).toLocaleString()}`
            : 'N/A';

          return {
            id: poDetails.id || Math.random(),
            poNumber: poNumber,
            poDate: poDetails.po_date || 'N/A',
            poStatus: poStatus,
            vendor: poDetails.finalized_vendor || firstInvoice?.invoiceDetails?.vendor || 'N/A',
            poLength: poLength,
            item: poDetails.item || 'N/A',
            type: poDetails.type || 'N/A',
            unitRate: unitRate,
            city: poDetails.city || 'N/A',
            region: region, // Keep for filtering purposes
            // Store all invoices for this PO
            _invoices: invoices,
            _poDetails: poDetails,
          };
        });

        setWorkflowData(mappedData);
      } catch (error) {
        console.error('Error fetching POs with invoices:', error);
        setWorkflowData([]);
        setRawData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPOs();
  }, [selectedTab]); // Refetch when tab changes

  // Get unique values for filters from workflow data
  const dates = useMemo(() => {
    const uniqueDates = [...new Set(workflowData.map((item) => item.poDate).filter(Boolean))];
    return uniqueDates.sort();
  }, [workflowData]);

  const cities = useMemo(() => {
    const uniqueCities = [...new Set(workflowData.map((item) => item.city).filter(Boolean))];
    return uniqueCities.sort();
  }, [workflowData]);

  const vendors = useMemo(() => {
    const uniqueVendors = [...new Set(workflowData.map((item) => item.vendor).filter(Boolean))];
    return uniqueVendors.sort();
  }, [workflowData]);

  // All Invoice tab: filter by date/status and map flat API response to table rows
  const allInvoicesWorkflowData = useMemo(() => {
    if (
      selectedTab !== 'all-invoices-full' ||
      !Array.isArray(allInvoicesData) ||
      allInvoicesData.length === 0
    ) {
      return [];
    }
    let filtered = allInvoicesData;
    // Filter by Invoice Date (created_at) - date-only comparison
    if (allInvoicesDateFrom || allInvoicesDateTo) {
      filtered = filtered.filter((inv) => {
        const invDate = inv.created_at ? dayjs(inv.created_at) : null;
        if (!invDate || !invDate.isValid()) {
          return false;
        }
        const invDateStr = invDate.format('YYYY-MM-DD');
        if (allInvoicesDateFrom) {
          const fromStr = dayjs(allInvoicesDateFrom).format('YYYY-MM-DD');
          if (invDateStr < fromStr) {
            return false;
          }
        }
        if (allInvoicesDateTo) {
          const toStr = dayjs(allInvoicesDateTo).format('YYYY-MM-DD');
          if (invDateStr > toStr) {
            return false;
          }
        }
        return true;
      });
    }
    if (allInvoicesStatus && allInvoicesStatus.trim() !== '') {
      const statusLower = allInvoicesStatus.toLowerCase().trim();
      filtered = filtered.filter((inv) => (inv.status || '').toLowerCase() === statusLower);
    }
    return filtered.map((inv) => {
      const created = inv.created_at ? dayjs(inv.created_at).format('YYYY-MM-DD') : 'N/A';
      const status = (inv.status || '').trim();
      const statusDisplay = status
        ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        : 'N/A';
      const amount =
        inv.invoice_amount != null
          ? Number(inv.invoice_amount).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : 'N/A';
      return {
        id: inv.po_id,
        invoiceId: inv.id,
        invoiceNumber: inv.invoice_number || 'N/A',
        invoiceDate: created,
        poNumber: inv.po_number || 'N/A',
        vendor: inv.vendor || 'N/A',
        paymentMilestone:
          inv.payment_milestone != null && String(inv.payment_milestone).trim() !== ''
            ? String(inv.payment_milestone).trim()
            : 'N/A',
        status: statusDisplay,
        invoiceAmount: amount,
        city: inv.po_city || inv.region || 'N/A',
        _rawInvoice: inv, // full API record for invoice details page (documents, PO file)
        _invoices: [
          {
            invoiceDetails: {
              id: inv.id,
              invoice_number: inv.invoice_number,
              created_at: inv.created_at,
              status: inv.status,
              vendor: inv.vendor,
              invoice_amount: inv.invoice_amount,
              region: inv.region,
              payment_milestone: inv.payment_milestone,
              reason_of_rejection: inv.reason_of_rejection,
              previous_assigned_role: inv.previous_assigned_role,
            },
            assignedRole: inv.assigned_role || {},
          },
        ],
        _poDetails: inv.po_id
          ? {
              id: inv.po_id,
              po: inv.po_number || inv.po_record_number || null,
              po_date: inv.po_date || null,
              city: inv.po_city || null,
              finalized_vendor: inv.po_finalized_vendor || inv.vendor || null,
              item: inv.po_item ?? null,
              type: inv.po_type ?? null,
              final_length_km: inv.po_final_length_km ?? null,
              final_length_meters: inv.po_final_length_meters ?? null,
              unit_rate_meter: inv.po_unit_rate_meter ?? null,
              total_cost_without_pst: inv.po_total_cost_without_pst ?? null,
            }
          : null,
      };
    });
  }, [selectedTab, allInvoicesData, allInvoicesDateFrom, allInvoicesDateTo, allInvoicesStatus]);

  // Map invoice data from stored invoices
  const mapInvoiceData = (invoices, poDetails) => {
    if (!invoices || invoices.length === 0) {
      return [];
    }

    // Format date
    const formatDate = (dateString) => {
      if (!dateString) {
        return 'N/A';
      }
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch {
        return dateString;
      }
    };

    const statusLabel = (status) => {
      if (!status) {
        return 'N/A';
      }
      const raw = status.toString().toLowerCase().trim();
      if (raw === 'pending_rejection') {
        return 'Rejection Pending';
      }
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    };

    return invoices.map((item) => {
      const invoice = item.invoiceDetails || item.invoice_details || {};
      const assignedRole = item.assignedRole || item.assigned_role || {};
      const previousAssignedRole =
        invoice.previous_assigned_role || item.invoice_details?.previous_assigned_role;

      return {
        id: invoice.id || Math.random(),
        invoiceNumber: invoice.invoice_number || 'N/A',
        invoiceDate: formatDate(invoice.created_at || invoice.updated_at),
        invoiceAmount: invoice.invoice_amount || 0,
        reasonOfRejection: invoice.reason_of_rejection || '',
        milestone: invoice.payment_milestone || 'N/A',
        statusRaw: (invoice.status || '').toString().toLowerCase().trim(),
        approvalStatus: statusLabel(invoice.status),
        currentApprover: assignedRole.name || 'N/A',
        previousApprover: previousAssignedRole?.name || 'N/A',
        vendor: invoice.vendor || poDetails?.finalized_vendor || 'N/A',
        region: invoice.region || assignedRole.region || 'N/A',
        city: poDetails?.city || 'N/A',
        _originalData: invoice,
      };
    });
  };

  // Open invoices page for a PO (used by both PO Number click and three-dots menu)
  const handleOpenPOInvoices = useCallback(
    (poData) => {
      const invoices = poData._invoices ? mapInvoiceData(poData._invoices, poData._poDetails) : [];
      navigate('/poWorkflowManagement/invoices', {
        state: {
          poNumber: poData.poNumber,
          poId: poData.id,
          invoices,
          poDetails: poData._poDetails || null,
          fromTab: selectedTab,
        },
      });
    },
    [navigate, selectedTab]
  );

  // Open View modal: show PO details and fetch ring details for the PO
  const handleViewClick = useCallback(async (poData) => {
    if (!poData?.id) {
      return;
    } // No PO linked (e.g. some All Invoice rows)
    setViewModal((prev) => ({ ...prev, open: true, poData, rings: [], ringsLoading: true }));
    try {
      const [ringsResult, fullPo] = await Promise.all([
        getPoRingsDetail(poData.id),
        getAuthorizedPoById(poData.id),
      ]);
      const rings = ringsResult?.data ?? [];
      const enrichedPoData = fullPo
        ? {
            ...poData,
            _poDetails: {
              ...(poData._poDetails || {}),
              id: fullPo.id ?? poData._poDetails?.id ?? poData.id,
              po: fullPo.po ?? poData._poDetails?.po ?? poData.poNumber ?? null,
              po_date: fullPo.po_date ?? poData._poDetails?.po_date ?? null,
              city: fullPo.city ?? poData._poDetails?.city ?? null,
              finalized_vendor:
                fullPo.finalized_vendor ?? poData._poDetails?.finalized_vendor ?? null,
              item: fullPo.item ?? poData._poDetails?.item ?? null,
              type: fullPo.type ?? poData._poDetails?.type ?? null,
              final_length_km: fullPo.final_length_km ?? poData._poDetails?.final_length_km ?? null,
              final_length_meters:
                fullPo.final_length_meters ?? poData._poDetails?.final_length_meters ?? null,
              unit_rate_meter: fullPo.unit_rate_meter ?? poData._poDetails?.unit_rate_meter ?? null,
              total_cost_without_pst:
                fullPo.total_cost_without_pst ?? poData._poDetails?.total_cost_without_pst ?? null,
              account: fullPo.account ?? poData._poDetails?.account ?? null,
              pr: fullPo.pr ?? poData._poDetails?.pr ?? null,
            },
          }
        : poData;
      setViewModal((prev) => ({
        ...prev,
        poData: enrichedPoData,
        rings,
        ringsLoading: false,
      }));
    } catch (err) {
      setViewModal((prev) => ({ ...prev, rings: [], ringsLoading: false }));
    }
  }, []);

  // Open full invoice details page (with documents and PO file) from All Invoices tab
  const handleOpenInvoiceDetailsFromAll = useCallback(
    (rowData) => {
      const rawInvoice = rowData._rawInvoice;
      if (!rawInvoice) {
        return;
      }
      navigate('/poWorkflowManagement/invoices/details', {
        state: {
          invoiceData: rawInvoice,
          poNumber: rowData.poNumber || rawInvoice.po_number || '',
          invoices: rowData._invoices || [],
          fromTab: selectedTab,
        },
      });
    },
    [navigate, selectedTab]
  );

  const handleCloseViewModal = useCallback(() => {
    setViewModal({ open: false, poData: null, rings: [], ringsLoading: false });
  }, []);

  // Actions Menu Component
  // eslint-disable-next-line react/prop-types
  const ActionsMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleOpen = () => {
      handleClose();
      handleOpenPOInvoices(row.original);
    };

    const handleView = () => {
      handleClose();
      handleViewClick(row.original);
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{
            color: '#667085',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={handleView}
            sx={{
              gap: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiListItemIcon-root': {
                minWidth: '32px',
              },
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="PO & Ring Details" />
          </MenuItem>
          <MenuItem
            onClick={handleOpen}
            sx={{
              gap: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '& .MuiListItemIcon-root': {
                minWidth: '32px',
              },
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open" />
          </MenuItem>
        </Menu>
      </>
    );
  };

  // All Invoice tab: View (PO & rings modal) + View Invoice Details (full page with documents)
  // eslint-disable-next-line react/prop-types
  const AllInvoiceActionsMenu = ({ row, onView, onViewInvoiceDetails }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const canView = row.original._poDetails && row.original.id;
    const hasRawInvoice = !!row.original._rawInvoice;

    const handleClick = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleView = () => {
      handleClose();
      if (canView) {
        onView(row.original);
      }
    };

    const handleViewInvoiceDetails = () => {
      handleClose();
      if (hasRawInvoice && onViewInvoiceDetails) {
        onViewInvoiceDetails(row.original);
      }
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{
            color: '#667085',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={handleViewInvoiceDetails}
            disabled={!hasRawInvoice}
            sx={{
              gap: 0.5,
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              '& .MuiListItemIcon-root': { minWidth: '32px' },
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="View Invoice Details" />
          </MenuItem>
          <MenuItem
            onClick={handleView}
            disabled={!canView}
            sx={{
              gap: 0.5,
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              '& .MuiListItemIcon-root': { minWidth: '32px' },
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="View PO & Ring Details" />
          </MenuItem>
        </Menu>
      </>
    );
  };

  // Columns for All Invoice tab (one row per invoice from h_vendor_invoices)
  const invoiceColumns = useMemo(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice Number',
        size: 150,
        enableColumnActions: false,
        Cell: ({ cell, row }) => (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenInvoiceDetailsFromAll(row.original);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenInvoiceDetailsFromAll(row.original);
              }
            }}
            style={{
              fontSize: '14px',
              color: '#1976d2',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {cell.getValue() || 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: 'invoiceDate',
        header: 'Invoice Date',
        size: 120,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'poNumber',
        header: 'PO Number',
        size: 140,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 150,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'paymentMilestone',
        header: 'Payment milestone',
        size: 140,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 110,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'invoiceAmount',
        header: 'Invoice Amount',
        size: 130,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() ?? 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 120,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        header: 'Actions',
        enableColumnActions: false,
        size: 50,
        Cell: ({ row }) => (
          <AllInvoiceActionsMenu
            row={row}
            onView={handleViewClick}
            onViewInvoiceDetails={handleOpenInvoiceDetailsFromAll}
          />
        ),
      },
    ],
    [handleViewClick, handleOpenInvoiceDetailsFromAll]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'poNumber',
        header: 'PO Number',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const poNumber = cell.getValue();
          const rowData = cell.row.original;
          return (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPOInvoices(rowData);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenPOInvoices(rowData);
                }
              }}
              style={{
                fontSize: '14px',
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#1565c0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1976d2';
              }}
            >
              {poNumber || 'N/A'}
            </span>
          );
        },
      },
      {
        accessorKey: 'poDate',
        header: 'PO Date',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const date = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{date || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const vendor = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{vendor || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'poLength',
        header: 'PO Length',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const length = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{length || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'item',
        header: 'Item',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const item = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{item || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const type = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{type || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'unitRate',
        header: 'Unit Rate',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const unitRate = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{unitRate || 'N/A'}</span>;
        },
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 150,
        enableColumnActions: false,
        // eslint-disable-next-line react/prop-types
        Cell: ({ cell }) => {
          // eslint-disable-next-line react/prop-types
          const city = cell.getValue();
          return <span style={{ fontSize: '14px', color: '#667085' }}>{city || 'N/A'}</span>;
        },
      },
      {
        header: 'Actions',
        enableColumnActions: false,
        size: 50,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <ActionsMenu row={row} />,
      },
    ],
    [handleOpenPOInvoices, handleViewClick]
  );

  const tableColumns = selectedTab === 'all-invoices-full' ? invoiceColumns : columns;

  // Filter data based on search term and filters
  const filteredData = useMemo(() => {
    if (selectedTab === 'all-invoices-full') {
      let filtered = allInvoicesWorkflowData;
      if (searchTerm) {
        filtered = filtered.filter((item) =>
          Object.values(item).some(
            (val) => val != null && String(val).toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }
      return filtered;
    }
    let filtered = workflowData;
    if (selectedDate) {
      filtered = filtered.filter((item) => item.poDate === selectedDate);
    }
    if (selectedCity) {
      filtered = filtered.filter((item) => item.city === selectedCity);
    }
    if (selectedVendor) {
      filtered = filtered.filter((item) => item.vendor === selectedVendor);
    }
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        Object.values(item).some(
          (val) => val != null && String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    return filtered;
  }, [
    searchTerm,
    selectedDate,
    selectedCity,
    selectedVendor,
    workflowData,
    selectedTab,
    allInvoicesWorkflowData,
  ]);

  // Reset pagination to first page when filters or tab change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    searchTerm,
    selectedDate,
    selectedCity,
    selectedVendor,
    selectedTab,
    allInvoicesDateFrom,
    allInvoicesDateTo,
    allInvoicesStatus,
  ]);

  // Download Excel: All Invoices = shaped invoice export; PO tabs = one row per invoice with full nested JSON
  const handleDownloadExcel = useCallback(
    (tableInstance) => {
      let dataToDownload = filteredData;
      if (tableInstance) {
        const filteredRows = tableInstance.getFilteredRowModel().rows;
        if (filteredRows.length > 0) {
          dataToDownload = filteredRows.map((row) => row.original);
        }
      }
      if (!Array.isArray(dataToDownload) || dataToDownload.length === 0) {
        return;
      }
      try {
        const excelDataRaw =
          selectedTab === 'all-invoices-full'
            ? dataToDownload.map((item) =>
                objectToExcelRow(mapAllVendorInvoiceForExcelExport(item._rawInvoice || {}))
              )
            : dataToDownload.flatMap((item) => expandPoWorkflowRowsForExport(item));
        if (!excelDataRaw.length) {
          return;
        }
        const excelData = alignExcelRows(excelDataRaw);
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        const { filename: exportLabel, sheet: sheetName } = getTabExportMeta(selectedTab);
        const currentDate = new Date().toISOString().split('T')[0];
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        XLSX.writeFile(workbook, `${exportLabel}_${currentDate}.xlsx`);
      } catch (error) {
        console.error('Error downloading Excel file:', error);
      }
    },
    [filteredData, selectedTab]
  );

  const table = useMaterialReactTable({
    columns: tableColumns,
    data: Array.isArray(filteredData) ? filteredData : [],
    state: {
      isLoading: loading,
      pagination,
    },
    onPaginationChange: setPagination,
    muiCircularProgressProps: {
      color: 'inherit',
      size: 40,
      sx: { color: '#999999' },
    },
    enableGlobalFilter: false,
    enableGlobalFilterModes: false,
    positionGlobalFilter: 'none',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableRowSelection: false,
    enableSelectAll: false,
    enableColumnPinning: false,
    enableFacetedValues: false,
    enableSorting: false,
    enableDensityToggle: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      columnOrder: [
        'poNumber',
        'poDate',
        'vendor',
        'poLength',
        'item',
        'type',
        'unitRate',
        'city',
        'actions',
      ],
    },
    manualPagination: false,
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30, 40],
      shape: 'rounded',
      variant: 'outlined',
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: '#BD1D23',
          color: 'white',
        },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableBodyRowProps: {
      sx: {
        height: 52,
        minHeight: 52,
        maxHeight: 52,
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        padding: '0 12px',
        '&:first-of-type': {
          paddingLeft: '12px !important',
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '125%',
        height: '56px',
        padding: '0 12px',
        alignItems: 'center',
        '&:first-of-type': {
          paddingLeft: '18px !important',
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        height: 56,
        '& .MuiTableCell-root': {
          padding: '6px 12px',
          verticalAlign: 'middle',
        },
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side - Search field */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            sx={{
              backgroundColor: '#fff',
              width: '300px',
              '& .MuiInputBase-input': {
                color: 'gray',
                '::placeholder': {
                  color: '#b3b9c4',
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                border: '1px solid #ccc',
                borderRadius: '10px',
                paddingRight: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Right side - Filter dropdowns and Download button */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {selectedTab === 'all-invoices-full' ? (
            <>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Invoice date from"
                  value={allInvoicesDateFrom ? dayjs(allInvoicesDateFrom) : null}
                  onChange={(v) => setAllInvoicesDateFrom(v ? v.toDate() : null)}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      placeholder: 'DD-MM-YYYY',
                      InputLabelProps: { shrink: true },
                      sx: {
                        minWidth: 168,
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                        '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="Invoice date to"
                  value={allInvoicesDateTo ? dayjs(allInvoicesDateTo) : null}
                  onChange={(v) => setAllInvoicesDateTo(v ? v.toDate() : null)}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      placeholder: 'DD-MM-YYYY',
                      InputLabelProps: { shrink: true },
                      sx: {
                        minWidth: 168,
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                        '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              <TextField
                select
                label="Status"
                size="small"
                value={allInvoicesStatus}
                onChange={(e) => setAllInvoicesStatus(e.target.value)}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : 'All statuses',
                }}
                sx={{
                  minWidth: 168,
                  backgroundColor: '#fff',
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                  '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                }}
              >
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="stop">Stopped</MenuItem>
                <MenuItem value="reverted">Reverted</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </TextField>
            </>
          ) : null}
          {selectedTab !== 'all-invoices-full' ? (
            <>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    color: '#757575',
                    borderRadius: '10px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                    '&:hover': {
                      border: '1px solid #ccc',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #ccc',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      borderBottom: '1px solid #eee',
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                    }}
                  >
                    DATE
                  </MenuItem>
                  {dates.map((date, index) => (
                    <MenuItem
                      key={date}
                      value={date}
                      sx={{
                        fontSize: '13px',
                        color: '#656e7a',
                        lineHeight: 1.4,
                        borderBottom: index !== dates.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {date}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    color: '#757575',
                    borderRadius: '10px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                    '&:hover': {
                      border: '1px solid #ccc',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #ccc',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      borderBottom: '1px solid #eee',
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                    }}
                  >
                    CITY
                  </MenuItem>
                  {cities.map((city, index) => (
                    <MenuItem
                      key={city}
                      value={city}
                      sx={{
                        fontSize: '13px',
                        color: '#656e7a',
                        lineHeight: 1.4,
                        borderBottom: index !== cities.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Vendor Dropdown */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    color: '#757575',
                    borderRadius: '10px',
                    '& .MuiSelect-select': {
                      padding: '8px 12px',
                    },
                    '&:hover': {
                      border: '1px solid #ccc',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #ccc',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      borderBottom: '1px solid #eee',
                      fontSize: '13px',
                      color: '#656e7a',
                      lineHeight: 1.4,
                    }}
                  >
                    VENDOR
                  </MenuItem>
                  {vendors.map((vendor, index) => (
                    <MenuItem
                      key={vendor}
                      value={vendor}
                      sx={{
                        fontSize: '13px',
                        color: '#656e7a',
                        lineHeight: 1.4,
                        borderBottom: index !== vendors.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      {vendor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : null}

          {/* Download button */}
          <Button
            variant="outlined"
            onClick={() => handleDownloadExcel(table)}
            disabled={filteredData.length === 0}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              color: '#333',
              borderRadius: '10px',
              minWidth: 'auto',
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid #ccc',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                color: '#999',
                opacity: 0.6,
              },
            }}
          >
            <DownloadIcon />
          </Button>
        </Box>
      </Box>
    ),
  });

  const poDetails = viewModal.poData?._poDetails || {};
  const formatPoLength = () => {
    if (poDetails.final_length_km) {
      return `${poDetails.final_length_km} km`;
    }
    if (poDetails.final_length_meters) {
      return `${(Number(poDetails.final_length_meters) / 1000).toFixed(2)} km`;
    }
    return 'N/A';
  };

  const ringTotals = useMemo(() => {
    const parseNum = (val) => {
      if (val === null || val === undefined) {
        return 0;
      }
      const cleaned = String(val).replace(/,/g, '').trim();
      const num = cleaned && !Number.isNaN(parseFloat(cleaned)) ? parseFloat(cleaned) : 0;
      return num;
    };

    let total = 0;
    let onNet = 0;
    let offNet = 0;

    (viewModal.rings || []).forEach((ring) => {
      total += parseNum(ring.total_length_meters);
      onNet += parseNum(ring.on_net_meters);
      offNet += parseNum(ring.off_net_meters);
    });

    return { total, onNet, offNet };
  }, [viewModal.rings]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <MaterialReactTable table={table} />
      <Dialog
        open={viewModal.open}
        onClose={handleCloseViewModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1.5 }}>
          PO Details & Ring Details
          {viewModal.poData?.poNumber && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              — {viewModal.poData.poNumber}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {viewModal.poData && (
            <>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                PO Information
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 1.5,
                  mb: 3,
                  p: 1.5,
                  bgcolor: '#fafafa',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PO Number
                  </Typography>
                  <br />
                  <Typography variant="body2">{poDetails.po ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PO Date
                  </Typography>
                  <br />
                  <Typography variant="body2">{poDetails.po_date ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Vendor
                  </Typography>
                  <br />
                  <Typography variant="body2">{poDetails.finalized_vendor ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    City
                  </Typography>
                  <br />
                  <Typography variant="body2">{poDetails.city ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Item
                  </Typography>
                  <br />
                  <Typography variant="body2">{poDetails.item ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <br />
                  <Typography variant="body2">{poDetails.type ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PO Length
                  </Typography>
                  <br />
                  <Typography variant="body2">{formatPoLength()}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Unit Rate (per m)
                  </Typography>
                  <br />
                  <Typography variant="body2">
                    {poDetails.unit_rate_meter != null
                      ? `PKR ${Number(poDetails.unit_rate_meter).toLocaleString()}`
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Cost
                  </Typography>
                  <br />
                  <Typography variant="body2">
                    {poDetails.total_cost_without_pst != null
                      ? `PKR ${Number(poDetails.total_cost_without_pst).toLocaleString()}`
                      : 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Ring Details
              </Typography>
              {viewModal.ringsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : viewModal.rings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No ring details uploaded for this PO.
                </Typography>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Ring Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            Total Length (m)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            On Net (m)
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">
                            Off Net (m)
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {viewModal.rings.map((ring, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{ring.ring_unique_name ?? '—'}</TableCell>
                            <TableCell align="right">{ring.total_length_meters ?? '—'}</TableCell>
                            <TableCell align="right">{ring.on_net_meters ?? '—'}</TableCell>
                            <TableCell align="right">{ring.off_net_meters ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderTop: '1px solid rgba(224, 224, 224, 1)',
                            }}
                          >
                            Totals
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              borderTop: '1px solid rgba(224, 224, 224, 1)',
                            }}
                          >
                            {ringTotals.total.toLocaleString()} m
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              borderTop: '1px solid rgba(224, 224, 224, 1)',
                            }}
                          >
                            {ringTotals.onNet.toLocaleString()} m
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              borderTop: '1px solid rgba(224, 224, 224, 1)',
                            }}
                          >
                            {ringTotals.offNet.toLocaleString()} m
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <Box sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleCloseViewModal}>
            Close
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};
